import { Box, Button, Card, CardActionArea, CardMedia, Container, Stack, Typography } from "@mui/material";
import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import "moment/locale/fr";
import searchService, { PublicProfilesCarouselItem } from "services/searchService";
import { getImageUrl } from "utils/imageUtils";
import { toQueryParams } from "helpers/queryHelpers";

moment.locale("fr");

const PublicProfilesCarousel: FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<PublicProfilesCarouselItem[]>([]);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await searchService.getPublicCarousel(6);
        setItems(response ?? []);
      } catch {
        setItems([]);
      }
    };
    load();
  }, []);

  const goToSearch = (item: PublicProfilesCarouselItem) => {
    const query = toQueryParams({
      destinationId: item.destinationId,
      from: moment(new Date(item.from)).format("YYYY-MM-DD HH:mm:ss"),
      to: moment(new Date(item.to)).format("YYYY-MM-DD HH:mm:ss"),
      stayTypes: [],
      budgets: [],
      durations: [],
      transports: [],
    });
    navigate(`/liste-profils?${query}`);
  };

  if (!items.length) return null;

  return (
    <Container sx={{ py: 5 }}>
      <Typography variant="h4" fontWeight={700} mb={1}>
        Ils partent
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={3}>
        Les voyageurs qui n'attendent que vous pour partager des souvenirs
      </Typography>
      <Stack
        direction="row"
        spacing={2}
        sx={{
          overflowX: "auto",
          pb: 1,
          scrollSnapType: "x mandatory",
          "& > *": { scrollSnapAlign: "start" }
        }}
      >
        {items.map((item, idx) => {
          const firstName = (item.firstName ?? "").trim() || "·";

          return (
          <Card
            key={`${item.destinationId}-${item.from}-${idx}`}
            sx={{
              flex: "0 0 calc((100% - 16px) / 2)",
              minWidth: { xs: "85vw", md: 360 },
              maxWidth: 560,
              borderRadius: 4,
              overflow: "hidden",
              position: "relative",
              boxShadow: "0 14px 30px rgba(0, 0, 0, 0.16)"
            }}
          >
            <CardActionArea onClick={() => goToSearch(item)} sx={{ position: "relative" }}>
              <CardMedia
                component="img"
                height="300"
                image={getImageUrl(item.blurredProfilePicture)}
                alt={firstName}
                sx={{ objectFit: "cover" }}
              />

              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: 0,
                  p: 2,
                  color: "#F7F8FA",
                  background: "linear-gradient(to top, rgba(8, 12, 20, 0.92), rgba(8, 12, 20, 0.2) 60%, rgba(8, 12, 20, 0))",
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                  <Typography variant="h6" sx={{ color: "#FFFFFF", fontWeight: 700 }}>
                    {firstName}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)" }}>
                    {item.destinationName}
                  </Typography>
                </Box>

                <Typography variant="body2" sx={{ mt: 0.5, color: "rgba(255,255,255,0.88)" }}>
                  {moment(new Date(item.from)).format("ll")} to {moment(new Date(item.to)).format("ll")}
                </Typography>

                <Button
                  size="small"
                  variant="contained"
                  onClick={(e) => {
                    e.stopPropagation();
                    goToSearch(item);
                  }}
                  sx={{
                    mt: 1.25,
                    bgcolor: "#FFFFFF",
                    color: "#111827",
                    fontWeight: 700,
                    textTransform: "none",
                    "&:hover": { bgcolor: "#F1F5F9" }
                  }}
                >
                  Rechercher
                </Button>
              </Box>
            </CardActionArea>
          </Card>
          );
        })}
      </Stack>
    </Container>
  );
};

export default PublicProfilesCarousel;
